/* ==========================================================================
   Stories Reels — inline shoppable reels carousel
   - One instance per [data-strl] root; no shared state, no Flickity.
   - The centred reel is found on scroll (rAF-throttled) and is the only one
     that plays or shows its product overlay.
   - Endless: once scrolling settles, the strip is rotated in the DOM so the
     playing reel always sits in the middle with reels on both sides. Rotating
     real nodes (rather than cloning) keeps one <video> per reel.
   - Clicking a reel hands it to the shared fullscreen stories viewer
     (assets/stories-gallery.js) via window.StoriesViewer.
   - Theme-editor aware: re-inits on section load, centres a selected block.
   Markup + settings: sections/stories-reels.liquid   Styles: stories-reels.css
   ========================================================================== */
(function () {
  'use strict';

  var SETTLE_MS = 120; // quiet time after the last scroll event before rotating

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initReel(root) {
    if (!root || root.__strl) return;

    var viewport = root.querySelector('[data-strl-viewport]');
    var track = root.querySelector('[data-strl-track]');
    var endSpacer = root.querySelector('[data-strl-spacer-end]');
    if (!viewport || !track) return;

    var slides = [];
    var allSlides = Array.prototype.slice.call(track.querySelectorAll('[data-strl-slide]'));
    if (!allSlides.length) return;

    var dotsWrap = root.querySelector('[data-strl-dots]');
    var uid = root.getAttribute('data-strl-uid') || '';
    var autoplay = root.getAttribute('data-strl-autoplay') !== 'false';
    var muted = root.getAttribute('data-strl-muted') !== 'false';
    var autoAdvance = root.getAttribute('data-strl-advance') !== 'false';
    var imageMs = (parseFloat(root.getAttribute('data-strl-image-duration')) || 5) * 1000;
    // Rotating needs a reel to spare on each side, so looping starts at three.
    var loop = allSlides.length >= 3;

    var selectedEl = null;
    var userPaused = false; // set by the play/pause button, cleared on reel change
    var inView = true;
    var blocked = false; // a quick view / cart drawer is covering the carousel
    var rafId = null;
    var settleTimer = null;
    var imageTimer = null;
    var imageStartedAt = 0;
    var imageElapsed = 0; // image time already served, so a pause resumes mid-count
    var dots = [];

    function refreshSlides() {
      slides = Array.prototype.slice.call(track.querySelectorAll('[data-strl-slide]'));
    }

    // ---- Helpers --------------------------------------------------------

    function videoOf(slide) {
      return slide.querySelector('video');
    }

    // The reel whose centre sits closest to the centre of the viewport.
    function nearestSlide() {
      var vr = viewport.getBoundingClientRect();
      var centre = vr.left + vr.width / 2;
      var best = slides[0];
      var bestDist = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var r = slides[i].getBoundingClientRect();
        var d = Math.abs(r.left + r.width / 2 - centre);
        if (d < bestDist) {
          bestDist = d;
          best = slides[i];
        }
      }
      return best;
    }

    function play(v) {
      var p = v.play();
      if (p && p.catch) {
        p.catch(function () {
          // Autoplay with sound was refused — fall back to muted and retry.
          if (!v.muted) {
            muted = true;
            v.muted = true;
            syncSoundButtons();
            var retry = v.play();
            if (retry && retry.catch) retry.catch(function () {});
          }
        });
      }
    }

    // ---- Sync -----------------------------------------------------------

    // Whether the centred reel should be running right now.
    function canRun() {
      return inView && !blocked && !userPaused;
    }

    function applyPlayback() {
      for (var i = 0; i < allSlides.length; i++) {
        var v = videoOf(allSlides[i]);
        if (!v) continue;
        if (allSlides[i] === selectedEl) {
          v.muted = muted;
          if (autoplay && canRun()) {
            play(v);
          } else {
            v.pause();
          }
        } else {
          v.muted = true;
          v.pause();
          try {
            v.currentTime = 0;
          } catch (e) {}
        }
      }
      syncSoundButtons();
      updateImageTimer();
    }

    // ---- Auto-advance ---------------------------------------------------

    // Image reels have no 'ended' event, so they run a timer instead. It is
    // frozen rather than cancelled whenever the reel stops, so closing a quick
    // view resumes the count where it left off.
    function freezeImageTimer() {
      if (!imageTimer) return;
      imageElapsed += Date.now() - imageStartedAt;
      clearTimeout(imageTimer);
      imageTimer = null;
    }

    function updateImageTimer() {
      var wanted = autoAdvance && selectedEl && !videoOf(selectedEl) && canRun();
      if (!wanted) {
        freezeImageTimer();
        return;
      }
      if (imageTimer) return;
      imageStartedAt = Date.now();
      imageTimer = setTimeout(function () {
        imageTimer = null;
        imageElapsed = 0;
        advance();
      }, Math.max(0, imageMs - imageElapsed));
    }

    function advance() {
      if (!autoAdvance || !canRun()) return;
      var at = slides.indexOf(selectedEl);
      var next = slides[at + 1] || slides[0];
      if (next && next !== selectedEl) scrollToSlide(next);
    }

    function syncSoundButtons() {
      var buttons = root.querySelectorAll('[data-strl-sound]');
      for (var i = 0; i < buttons.length; i++) {
        buttons[i].classList.toggle('is-muted', muted);
        buttons[i].setAttribute('aria-pressed', muted ? 'true' : 'false');
        buttons[i].setAttribute(
          'aria-label',
          buttons[i].getAttribute(muted ? 'data-strl-label-unmute' : 'data-strl-label-mute') || ''
        );
      }
    }

    function syncPlayButton(slide) {
      var button = slide.querySelector('[data-strl-playpause]');
      var v = videoOf(slide);
      if (!button || !v) return;
      button.classList.toggle('is-paused', v.paused);
      button.setAttribute(
        'aria-label',
        button.getAttribute(v.paused ? 'data-strl-label-play' : 'data-strl-label-pause') || ''
      );
    }

    function indexOfSlide(slide) {
      return parseInt(slide.getAttribute('data-strl-index'), 10) || 0;
    }

    function select(slide) {
      if (!slide || slide === selectedEl) return;
      if (selectedEl) selectedEl.classList.remove('is-selected');
      selectedEl = slide;
      slide.classList.add('is-selected');
      userPaused = false;
      freezeImageTimer();
      imageElapsed = 0;
      var active = indexOfSlide(slide);
      for (var d = 0; d < dots.length; d++) {
        dots[d].classList.toggle('is-active', d === active);
        dots[d].setAttribute('aria-current', d === active ? 'true' : 'false');
      }
      applyPlayback();
    }

    function scrollToSlide(slide, instant) {
      if (!slide) return;
      var vr = viewport.getBoundingClientRect();
      var sr = slide.getBoundingClientRect();
      var left = viewport.scrollLeft + (sr.left + sr.width / 2 - (vr.left + vr.width / 2));
      if (!instant && viewport.scrollTo) {
        viewport.scrollTo({ left: left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      } else {
        viewport.scrollLeft = left;
      }
    }

    // ---- Fullscreen viewer ----------------------------------------------

    // Hand the reel over to the shared stories viewer (assets/stories-gallery.js),
    // which reads the playlist this section embedded as JSON. data-strl-story is
    // the reel's place in that playlist, and is absent on a reel with no media.
    function openFullscreen(slide) {
      var viewer = window.StoriesViewer;
      if (!viewer || !viewer.open || !uid) return false;
      var at = slide.getAttribute('data-strl-story');
      if (at === null) return false;
      var dataEl = document.querySelector(
        'script[type="application/json"][data-stg-data="' + uid + '"]'
      );
      if (!dataEl) return false;
      var list;
      try {
        list = JSON.parse(dataEl.textContent);
      } catch (err) {
        return false;
      }
      if (!Array.isArray(list) || !list.length) return false;
      viewer.open(list, parseInt(at, 10) || 0, uid, { loop: true });
      return true;
    }

    // Rotate the strip so the playing reel sits in the middle. Every reel is the
    // same width, so moving one from one end to the other and shifting
    // scrollLeft by that width leaves the view pixel-identical — the reels
    // either side simply refill.
    function normalize() {
      if (!loop || !selectedEl || slides.length < 3) return;
      var width = slides[0].offsetWidth;
      if (!width || !viewport.clientWidth) return;

      var shift = slides.indexOf(selectedEl) - Math.floor(slides.length / 2);
      if (!shift) return;

      // Rotating and then failing to move the scroll would jump the carousel, so
      // bail out while the container has no usable scroll range — a background
      // tab or a hidden ancestor has not laid it out yet. The observer and the
      // next settle both call back in.
      var landing = viewport.scrollLeft - shift * width;
      if (landing < -1 || landing > viewport.scrollWidth - viewport.clientWidth + 1) return;

      // Suspend snapping across the move: re-snapping after a DOM change can
      // otherwise pull the scroll offset back and jump the carousel a reel.
      viewport.style.scrollSnapType = 'none';

      var scrollLeft = viewport.scrollLeft;
      var i;
      if (shift > 0) {
        // Selected sits too far right — move leading reels to the tail.
        for (i = 0; i < shift; i++) track.insertBefore(slides[i], endSpacer);
        scrollLeft -= shift * width;
      } else {
        // Selected sits too far left — move trailing reels to the head. Walk the
        // tail block front-to-back, always inserting before the same anchor, so
        // the reels keep their running order.
        for (i = 0; i < -shift; i++) {
          track.insertBefore(slides[slides.length + shift + i], slides[0]);
        }
        scrollLeft += -shift * width;
      }
      viewport.scrollLeft = scrollLeft;
      // Read back to flush layout before snapping is handed back; the offset is
      // already on a snap point, so restoring it is a no-op.
      void viewport.scrollLeft;
      viewport.style.scrollSnapType = '';
      refreshSlides();
    }

    // ---- Dots -----------------------------------------------------------

    if (dotsWrap) {
      var gotoLabel = dotsWrap.getAttribute('data-strl-label-goto') || 'Go to reel';
      for (var d0 = 0; d0 < allSlides.length; d0++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'strl__dot';
        dot.setAttribute('data-strl-dot', String(d0));
        dot.setAttribute('aria-label', gotoLabel + ' ' + (d0 + 1));
        dotsWrap.appendChild(dot);
        dots.push(dot);
      }
    }

    // ---- Events ---------------------------------------------------------

    function onScroll() {
      if (!rafId) {
        rafId = requestAnimationFrame(function () {
          rafId = null;
          select(nearestSlide());
        });
      }
      clearTimeout(settleTimer);
      settleTimer = setTimeout(function () {
        select(nearestSlide());
        normalize();
      }, SETTLE_MS);
    }

    viewport.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    root.addEventListener('click', function (e) {
      if (!e.target.closest) return;

      var playBtn = e.target.closest('[data-strl-playpause]');
      if (playBtn && root.contains(playBtn)) {
        e.preventDefault();
        var slide = playBtn.closest('[data-strl-slide]');
        var v = slide && videoOf(slide);
        if (!v) return;
        if (v.paused) {
          userPaused = false;
          v.muted = muted;
          play(v);
        } else {
          userPaused = true;
          v.pause();
        }
        return;
      }

      var soundBtn = e.target.closest('[data-strl-sound]');
      if (soundBtn && root.contains(soundBtn)) {
        e.preventDefault();
        muted = !muted;
        var current = selectedEl && videoOf(selectedEl);
        if (current) {
          current.muted = muted;
          // Unmuting is a user gesture, so playback may resume with sound.
          if (!muted && current.paused && !userPaused) play(current);
        }
        syncSoundButtons();
        return;
      }

      var dotBtn = e.target.closest('[data-strl-dot]');
      if (dotBtn && root.contains(dotBtn)) {
        e.preventDefault();
        var wanted = parseInt(dotBtn.getAttribute('data-strl-dot'), 10) || 0;
        for (var i = 0; i < slides.length; i++) {
          if (indexOfSlide(slides[i]) === wanted) {
            scrollToSlide(slides[i]);
            break;
          }
        }
        return;
      }

      // Anything left in the product overlay — the title/thumbnail links and the
      // quick-add button — keeps its own behaviour.
      if (e.target.closest('.strl__product a') || e.target.closest('[data-quick-add]')) return;

      // Clicking any reel opens it fullscreen in the shared stories viewer.
      var clicked = e.target.closest('[data-strl-slide]');
      if (!clicked || !root.contains(clicked)) return;
      if (openFullscreen(clicked)) {
        e.preventDefault();
      } else if (clicked !== selectedEl) {
        // No viewer available (script blocked, or a reel with no media) — fall
        // back to bringing the reel to the centre.
        e.preventDefault();
        scrollToSlide(clicked);
      }
    });

    viewport.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      e.preventDefault();
      var at = slides.indexOf(selectedEl);
      scrollToSlide(slides[e.key === 'ArrowRight' ? at + 1 : at - 1]);
    });

    // Keep each play/pause button in step with its own video, however the
    // state changed (button, browser policy, tab visibility).
    allSlides.forEach(function (slide) {
      var v = videoOf(slide);
      if (!v) return;
      // A looping video never fires 'ended', so looping is only for the reel
      // that has nowhere to advance to.
      v.loop = !autoAdvance || allSlides.length < 2;
      var sync = function () {
        syncPlayButton(slide);
      };
      v.addEventListener('play', sync);
      v.addEventListener('pause', sync);
      v.addEventListener('ended', function () {
        if (slide === selectedEl) advance();
      });
      sync();
    });

    // A quick view, the cart drawer or the fullscreen viewer covering the carousel
    // holds the reel where it is; closing it picks the video or the image timer
    // back up.
    var blockers = [{ el: document.body, cls: 'stg-playing' }];
    var quickAddModal = document.querySelector('[data-quick-add-modal]');
    var cartDrawer = document.querySelector('[data-cart-drawer]');
    if (quickAddModal) blockers.push({ el: quickAddModal, cls: 'visible' });
    if (cartDrawer) blockers.push({ el: cartDrawer, cls: 'is-visible' });

    function readBlocked() {
      for (var b = 0; b < blockers.length; b++) {
        if (blockers[b].el.classList.contains(blockers[b].cls)) return true;
      }
      return false;
    }

    var blockObserver = null;
    if (blockers.length && 'MutationObserver' in window) {
      blockObserver = new MutationObserver(function () {
        var next = readBlocked();
        if (next === blocked) return;
        blocked = next;
        applyPlayback();
      });
      for (var b0 = 0; b0 < blockers.length; b0++) {
        blockObserver.observe(blockers[b0].el, { attributes: true, attributeFilter: ['class'] });
      }
      blocked = readBlocked();
    }

    // Stop playback while the section is off-screen.
    var observer = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        function (entries) {
          inView = entries[0].isIntersecting;
          applyPlayback();
          // First time the section is actually laid out and on screen, catch up
          // on any centring that was skipped.
          if (inView) normalize();
        },
        { threshold: 0.2 }
      );
      observer.observe(root);
    }

    root.__strl = {
      goToIndex: function (i) {
        for (var s = 0; s < slides.length; s++) {
          if (indexOfSlide(slides[s]) === i) return scrollToSlide(slides[s]);
        }
      },
      destroy: function () {
        viewport.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        if (observer) observer.disconnect();
        if (blockObserver) blockObserver.disconnect();
        if (rafId) cancelAnimationFrame(rafId);
        clearTimeout(settleTimer);
        clearTimeout(imageTimer);
        delete root.__strl;
      }
    };

    refreshSlides();
    // Always open on the first reel: with mandatory snapping the browser can
    // otherwise settle the container anywhere on its own.
    viewport.scrollLeft = 0;
    select(slides[0]);
    // Rotate straight away so that first reel gets neighbours on both sides.
    normalize();
  }

  function initAll(scope) {
    var host = scope && scope.querySelectorAll ? scope : document;
    if (host.matches && host.matches('[data-strl]')) initReel(host);
    var roots = host.querySelectorAll('[data-strl]');
    for (var i = 0; i < roots.length; i++) initReel(roots[i]);
  }

  function ready() {
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }

  // ---- Theme editor -----------------------------------------------------

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var roots = e.target.querySelectorAll ? e.target.querySelectorAll('[data-strl]') : [];
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__strl) roots[i].__strl.destroy();
    }
  });

  document.addEventListener('shopify:block:select', function (e) {
    if (!e.target.closest) return;
    var slide = e.target.closest('[data-strl-slide]');
    var root = slide && slide.closest('[data-strl]');
    if (!root || !root.__strl) return;
    root.__strl.goToIndex(parseInt(slide.getAttribute('data-strl-index'), 10) || 0);
  });
})();